import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { type DeliveryNoteDetailDto } from "@hisense/shared";
import { useMenuAccess } from "@/app/access";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { formatQty } from "@/domain/money";
import { formatDateTime } from "@/domain/datetime";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Input, FieldLabel } from "@/components/ui/Input";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";

/**
 * Nguồn hàng từng dòng phiếu giao (0088): xuất từ lô nào, hay giao thẳng kèm ghi chú. Chỉ hiện trong hệ thống — không in lên
 * phiếu giao gửi khách.
 *
 * Dòng giao thẳng trong lúc kho đang có hàng mã đó được Bảng điều khiển báo cho người có quyền Duyệt; người đó ghi nhận đã
 * xem (kèm ghi chú) ở đây thì hết báo. Người không có quyền Duyệt chỉ thấy trạng thái.
 */
export function DeliverySourcesPanel({ dn }: { dn: DeliveryNoteDetailDto }) {
  const { t, i18n } = useTranslation();
  const canDeliveryNote = useMenuAccess("deliveryNotes");
  const qc = useQueryClient();
  const [reviewing, setReviewing] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lines = dn.items.filter((it) => it.fulfillment);
  if (!lines.length) return null;
  // MỘT bộ giải quyền: `canAccess` đã luôn đúng với quản trị viên, nên không cần nhánh riêng.
  const canApprove = !dn.deletedAt && canDeliveryNote("approve");
  const save = async () => {
    if (busy || reviewing == null) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/delivery-notes/${dn.id}/direct-review/approve`, { method: "POST", body: { lineNo: reviewing, note: note.trim() } });
      await Promise.all([qc.invalidateQueries({ queryKey: ["dn", dn.id] }), qc.invalidateQueries({ queryKey: ["dashboard"] })]);
      setReviewing(null);
    } catch (e) {
      setError(getLocalizedError(e, t, "deliveryNotes.errorSave"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel className="mt-3 p-4">
      <h2 className="mb-1 font-medium">{t("deliveryWorkspace.sourcesTitle")}</h2>
      <p className="mb-3 text-xs text-ink-soft">{t("deliveryWorkspace.sourcesIntro")}</p>
      <TableShell>
        <thead>
          <tr>
            <Th align="right">{t("deliveryWorkspace.line")}</Th>
            <Th>{t("deliveryNotes.itemNo")}</Th>
            <Th align="right">{t("deliveryWorkspace.planned")}</Th>
            <Th>{t("deliveryWorkspace.fulfillment")}</Th>
            <Th>{t("deliveryWorkspace.stockLot")}</Th>
            <Th>{t("deliveryWorkspace.sourcesCheck")}</Th>
          </tr>
        </thead>
        <tbody>
          {lines.map((it) => {
            const flagged = it.fulfillment === "Direct" && Number(it.directStockQuantity) > 0;
            return (
              <Tr key={it.id}>
                <Td align="right" className="font-mono tabular text-xs">{it.lineNo}</Td>
                <Td className="font-mono text-xs">{it.itemNo}{it.subRef ? ` / ${it.subRef}` : ""}</Td>
                <Td align="right" className="font-mono tabular text-xs">{formatQty(it.orderedQty)} <span className="text-2xs text-ink-faint">{it.unit}</span></Td>
                <Td className="text-xs">{t(it.fulfillment === "Stock" ? "deliveryWorkspace.fulfillmentStock" : "deliveryWorkspace.fulfillmentDirect")}</Td>
                <Td className="max-w-80 whitespace-normal text-xs">{it.fulfillment === "Stock" ? <span className="font-mono">{it.lotNumber}</span> : it.fulfillmentNote}</Td>
                <Td className="whitespace-normal text-xs">
                  {!flagged ? <span className="text-ink-faint">—</span> : (
                    <div className="max-w-sm space-y-1">
                      <p className={it.directReviewedAt ? "text-ink-soft" : "text-warn"}>
                        {t("deliveryWorkspace.directWithStockAt", { qty: formatQty(Number(it.directStockQuantity)) })}
                      </p>
                      {it.directReviewedAt ? (
                        <p className="text-ink-soft">
                          {t("deliveryWorkspace.directReviewedBy", { name: it.directReviewedBy ?? "—" })} · {formatDateTime(it.directReviewedAt, i18n.language)}
                          {it.directReviewNote && <span className="block">{it.directReviewNote}</span>}
                        </p>
                      ) : canApprove ? (
                        <Button size="sm" variant="secondary" onClick={() => { setReviewing(it.lineNo); setNote(""); setError(null); }}>
                          {t("deliveryWorkspace.reviewDirect")}
                        </Button>
                      ) : (
                        <p className="text-ink-soft">{t("deliveryWorkspace.directNotReviewed")}</p>
                      )}
                    </div>
                  )}
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </TableShell>
      {reviewing != null && (
        <Modal title={t("deliveryWorkspace.reviewDirectTitle")} closeLabel={t("common.close")} onClose={() => setReviewing(null)}>
          <div className="space-y-3 p-5">
            <p className="text-sm">{dn.dnNo} · {t("deliveryWorkspace.line")} {reviewing}</p>
            <div>
              <FieldLabel htmlFor="direct-review-note">{t("deliveryWorkspace.reviewDirectNote")}</FieldLabel>
              <Input id="direct-review-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          </div>
          <div className="flex justify-end gap-2 border-t border-line p-4">
            <Button variant="secondary" onClick={() => setReviewing(null)}>{t("common.cancel")}</Button>
            <Button disabled={busy || note.trim().length < 5} onClick={() => void save()}>{t("deliveryWorkspace.reviewDirectSave")}</Button>
          </div>
        </Modal>
      )}
    </Panel>
  );
}
